import { useState, type ReactNode } from "react";
import { Minus, Plus, ExternalLink, XIcon } from "lucide-react";
import { usePlanner } from '../contexts/PlannerContext';
import { Dialog, DialogClose, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useCommunication } from '@/lib/CommunicationWrapper';
import { ScheduleEditor } from './ScheduleEditor';
import { RestrictedIcon } from '@/lib/restrictions';
import { GoogleSvg, MicrosoftSvg } from '@/lib/svg';
import { GatedCrown } from '@/components/GatedCrown';
//import { CalendarSyncGate } from '../calendar/CalendarSyncGate';


export const AutoPlanSettings = () => {

    const { settings, updateSettings } = usePlanner();
    const { settings: options } = useCommunication();

    const autoPlanSettings = (settings?.assignments_auto_plan ?? options?.assignments_auto_plan ?? { days: 0, time: "morning" }) as {
        days?: number;
        time?: string;
    };
    const daysValue = typeof autoPlanSettings.days === "number" && Number.isFinite(autoPlanSettings.days) ? autoPlanSettings.days : 0;
    const timeValue = typeof autoPlanSettings.time === "string" && autoPlanSettings.time.length > 0 ? autoPlanSettings.time : "morning";

    const handleChange = async (updates: any) => {
        updateSettings({ "assignments_auto_plan": { ...autoPlanSettings, ...updates } });
    }

    return (

        <div className="flex flex-col gap-[5px] p-[20px] text-theme-0">
            <h4 className="font-bold text-theme-0 text-xl">
                <span>Auto-plan</span>
                <GatedCrown highlight="assignments_auto_schedule" className="absolute -top-1 -right-2.5 size-2.5" />
            </h4>
            <p className="text-sm text-theme-1">Automatically plan your tasks based on your day's availability</p>
            <div className="mt-[20px]">
                <p className="font-semibold">Plan ahead</p>
                <div className="flex items-center gap-[5px]">
                    <input
                        type="number"
                        min={0}
                        className="w-[50px]"
                        value={daysValue}
                        onChange={(e) => {
                            const raw = e.target.value;
                            const parsed = raw === "" ? 0 : Number(raw);
                            if (!Number.isFinite(parsed)) return;
                            handleChange({ "days": Math.max(0, Math.floor(parsed)) });
                        }}
                    />
                    <span>Days before due</span>
                </div>
            </div>
            <div className="mt-[10px]">
                <p className="font-semibold">Time of day</p>
                <RadioGroup className="gap-[5px]" value={timeValue} onValueChange={(val: string) => handleChange({ "time": val })}>
                    <div className="flex items-center gap-[10px]">
                        <RadioGroupItem value="morning" id="autoplan-morning" />
                        <label htmlFor="autoplan-morning">Morning (8am-11pm)</label>
                    </div>
                    <div className="flex items-center gap-[10px]">
                        <RadioGroupItem value="afternoon" id="autoplan-afternoon" />
                        <label htmlFor="autoplan-afternoon">Afternoon (12pm-5pm)</label>
                    </div>
                    <div className="flex items-center gap-[10px]">
                        <RadioGroupItem value="evening" id="autoplan-evening" />
                        <label htmlFor="autoplan-evening">Evening (6pm-10pm)</label>
                    </div>
                </RadioGroup>
            </div>
        </div>
    )
}

export const AutoPlanSettingsDialog = ({
    trigger,
    onOpenChange,
}: {
    trigger: ReactNode;
    onOpenChange?: (open: boolean) => void;
}) => (
    <Dialog onOpenChange={onOpenChange}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent
            className="bg-theme-0 border border-theme rounded-lg shadow-lg text-theme-1 z-10000 w-max p-[4px] relative"
            showCloseButton={false}
        >
            <DialogClose className="ring-offset-background focus-visible:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs transition-opacity hover:opacity-70 focus-visible:ring-2 focus-visible:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
                <XIcon className="text-base-1" />
                <span className="sr-only">Close</span>
            </DialogClose>
            <AutoPlanSettings />
        </DialogContent>
    </Dialog>
);


type PlannerSettingsProps = {
    onDialogOpen?: () => void;
    onDialogClose?: () => void;
};

export const PlannerSettings = ({ onDialogOpen, onDialogClose }: PlannerSettingsProps) => {

    const { settings, setZoom } = usePlanner();
    const { loadSharedComponent } = useCommunication();

    const handleZoomChange = (delta: number) => {
        const currentZoom = settings.assignments_zoom || 100;
        const newZoom = Math.max(60, Math.min(200, currentZoom + delta));
        setZoom(newZoom);
    };

    const calendarViewType = settings.assignments_calendar_view_type || "week";

    return (
        <div className="flex flex-col gap-[2px]">
            {calendarViewType !== "month" && (
                <div className="text-sm flex items-center justify-between hover:bg-theme-1 gap-[60px] py-[7px] px-[10px] rounded-md">
                    <span>Zoom {settings.assignments_zoom || 100}%</span>
                    <div className="flex items-center gap-[5px]">
                        <button
                            onClick={() => handleZoomChange(-20)}
                            className="hover:bg-theme-2 rounded p-[2px]"
                        >
                            <Minus className="h-[14px] w-[14px]" />
                        </button>
                        <button
                            onClick={() => handleZoomChange(20)}
                            className="hover:bg-theme-2 rounded p-[2px]"
                        >
                            <Plus className="h-[14px] w-[14px]" />
                        </button>
                    </div>
                </div>
            )}
            <button
                className="text-sm flex items-center justify-between hover:bg-theme-1 gap-[60px] py-[7px] px-[10px] rounded-md"
                onClick={() => loadSharedComponent("CalendarSyncGate", { showCloseButton: true })}
            >
                <RestrictedIcon restrictedPlans={["none", "free", "trial", "trialThenPro"]} className="size-3 fill-current text-theme-0">
                    <span>Google Calendar Sync</span>
                </RestrictedIcon>
                <ExternalLink className="h-[16px] w-[16px]" />
            </button>
            <Dialog onOpenChange={(value) => value ? onDialogOpen?.() : onDialogClose?.()}>
                <DialogTrigger asChild>
                    <button className="text-sm flex items-center justify-between hover:bg-theme-1 gap-[60px] py-[7px] px-[10px] rounded-md">
                        <span>Class schedules</span>
                        <ExternalLink className="h-[16px] w-[16px]" />
                    </button>
                </DialogTrigger>
                <DialogContent
                    className="bg-theme-0 border border-theme rounded-lg shadow-lg text-theme-1 z-10000 w-max p-[4px] relative"
                    showCloseButton={false}
                >
                    <DialogClose className="ring-offset-background focus-visible:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs transition-opacity hover:opacity-70 focus-visible:ring-2 focus-visible:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
                        <XIcon className="text-base-1" />
                        <span className="sr-only">Close</span>
                    </DialogClose>
                    <ScheduleEditor />
                </DialogContent>
            </Dialog>
            <AutoPlanSettingsDialog
                onOpenChange={(value) => value ? onDialogOpen?.() : onDialogClose?.()}
                trigger={(
                    <button className="text-sm flex items-center justify-between hover:bg-theme-1 gap-[60px] py-[7px] px-[10px] rounded-md">
                        <div className="relative">
                            <span>Auto-plan settings</span>
                            <GatedCrown highlight="assignments_auto_schedule" className="absolute -top-1 -right-1 size-2.5" />
                        </div>
                        <ExternalLink className="h-[16px] w-[16px]" />
                    </button>
                )}
            />
        </div>
    )
}

export const MiniCalSettings = () => {

    const { settings, updateSettings, portal } = usePlanner();
    const { restrictedActionCheck, openRestrictedDialog, loadSharedComponent } = useCommunication();

    const [open, setOpen] = useState(false);



    return (
        <div className="flex flex-col gap-[2px] p-[5px]">
            <RadioGroup value={settings.assignments_minical_days.toString()} onValueChange={(value) => updateSettings({ "assignments_minical_days": parseInt(value) })} className="gap-0">
                <div className="flex items-center gap-[10px] py-[7px] px-[10px] cursor-pointer text-sm">
                    <RadioGroupItem value="1" id="minical-days-1" />
                    <label htmlFor="minical-days-1" className="cursor-pointer">1 day</label>
                </div>
                <div className="flex items-center gap-[10px] py-[7px] px-[10px] cursor-pointer text-sm">
                    <RadioGroupItem value="2" id="minical-days-2" />
                    <label htmlFor="minical-days-2" className="cursor-pointer">2 days</label>
                </div>
                <div className="flex items-center gap-[10px] py-[7px] px-[10px] cursor-pointer text-sm">
                    <RadioGroupItem value="3" id="minical-days-3" />
                    <label htmlFor="minical-days-3" className="cursor-pointer">3 days</label>
                </div>
            </RadioGroup>
            <button onClick={() => loadSharedComponent("CalendarSyncGate")} className="text-sm flex items-center justify-between hover:bg-theme-1 gap-[60px] py-3 px-[10px] rounded-md cursor-pointer">
                <div className="flex items-center gap-2">
                    <div className="flex items-center">
                        <div className="bg-theme-0 rounded-full p-0.5">
                            <MicrosoftSvg className="size-[15px]" />
                        </div>
                        <div className="-ml-1.5 bg-theme-0 rounded-full p-0.5">
                            <GoogleSvg className="size-[15px]" />
                        </div>
                    </div>
                    <p className="text-base-0">Calendar Sync</p>
                </div>
                <ExternalLink className="h-[16px] w-[16px]" />
            </button>
            <div onClick={() => loadSharedComponent("ScheduleEditor")} className="text-sm flex items-center justify-between hover:bg-theme-1 gap-[60px] py-3 px-[10px] rounded-md cursor-pointer">
                <span>Class schedules</span>
                <ExternalLink className="size-4" />
            </div>
            <div onClick={() => loadSharedComponent("AutoPlanSettings")} className="text-sm flex items-center justify-between hover:bg-theme-1 gap-[60px] py-3 px-[10px] rounded-md cursor-pointer">
                <div className="relative">
                    <span>Auto-plan settings</span>
                    <GatedCrown highlight="assignments_auto_schedule" className="absolute -top-1 -right-2.5 size-2.5" />
                </div>
                <ExternalLink className="size-4" />
            </div>
        </div >
    )
}
